import { useState } from "react";
import { api, ApiError, type Me } from "../lib/api";
import { useSession } from "../lib/session";
import { Button, Field } from "../components/primitives";
import { BrandLockup } from "../components/BrandLockup";

/**
 * Sign in.
 *
 * Grounded in ink: this is the firm's face, the one screen a client and a
 * consultant both meet, and it should look like the website they came from.
 *
 * Two things the server already decided and this screen must not undo:
 *
 *   * **Failure is uniform.** An unknown address, a wrong password and an
 *     account with no credential take the same path, cost the same time and
 *     return the same message. The screen shows that message verbatim rather
 *     than helpfully distinguishing them, because distinguishing them turns the
 *     form into an account enumerator.
 *
 *   * **MFA is mandatory for Super Admin, Partners and Leads.** The server
 *     refuses the password-only attempt for those roles with the same 401, so
 *     the code field is always offered rather than revealed after a failure —
 *     revealing it would disclose which addresses belong to privileged roles.
 */
export function SignIn() {
  const { refresh } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setPending(true);
    setProblem(null);

    try {
      await api.post<Me>("/api/session", {
        email: email.trim(),
        password,
        totp: totp.trim() || undefined,
      });
      await refresh();
    } catch (error) {
      setProblem(
        error instanceof ApiError
          ? error.message
          : "The platform could not be reached.",
      );
      setPassword("");
      setTotp("");
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      data-world="ink"
      className="flex min-h-screen flex-col bg-canvas text-ink"
    >
      <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-6 py-16 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-[1fr_22rem] lg:items-center lg:gap-24">
          {/* ---- the firm, stated once ---- */}
          <div className="max-w-[34ch]">
            <BrandLockup size="large" />
            <h1 className="mt-5 font-editorial text-3xl font-medium leading-[1.1] text-ink">
              Your engagement, in one place.
            </h1>
            <p className="mt-5 text-md leading-6 text-secondary">
              Where the work stands, what needs you, and everything released to
              you &mdash; without having to ask.
            </p>

            <div className="mt-10 border-t border-line pt-5">
              <p className="text-xs leading-5 text-muted">
                Trouble signing in? Your contact at Arikekpar &amp; Company can
                help.
              </p>
            </div>
          </div>

          {/* ---- the form ---- */}
          <form onSubmit={submit} className="flex flex-col gap-5 border-t border-line-strong pt-7 lg:border-l lg:border-t-0 lg:pl-9 lg:pt-0">
            <div>
              <h2 className="font-display text-lg font-medium tracking-title text-ink">
                Sign in
              </h2>
              <p className="mt-1 text-xs text-muted">
                Use the email address your engagement was set up with.
              </p>
            </div>

            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Field
              label="Authenticator code"
              // Always shown. Revealing it only after a failure would tell an
              // attacker which addresses hold privileged roles.
              hint="If you have been set up with an authenticator app."
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
            />

            {problem && (
              <p role="alert" className="border border-danger/40 bg-danger/[0.08] px-3 py-2 text-xs text-danger">
                {problem}
              </p>
            )}

            <Button tone="primary" type="submit" loading={pending} className="mt-1 w-full">
              {pending ? "Signing in" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>

      <footer className="border-t border-line px-6 py-5 lg:px-10">
        <p className="mx-auto max-w-5xl text-2xs uppercase tracking-label text-muted">
          A&amp;C 2.0 &middot; Authorised access only
        </p>
      </footer>
    </div>
  );
}
